"use client";

import { Sports, getPublicData } from "@web/lib/data";
import { toTitleCase } from "@web/lib/utils";
import { byFips, byInternet, byIso } from "country-code-lookup";
import { countries } from "country-flag-icons";
import flags from "country-flag-icons/react/3x2";
import { FC, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { Loader } from "./Loader";

type DataType = "teams" | "leagues" | "categories" | "seasons";
type SortDirection = "ascending" | "descending";
type ColumnDefinition = {
  id: string;
  size: number;
  align?: "start" | "center";
};

const rowsPerPage = 10;
const fetchLimit = rowsPerPage + 1;
const client = new QueryClient();
const dataTypes: DataType[] = ["teams", "leagues", "categories", "seasons"];

const dataTypeLabels: Record<DataType, string> = {
  teams: "Teams",
  leagues: "Leagues",
  categories: "Classes",
  seasons: "Seasons",
};

const searchPlaceholderByType: Record<DataType, string> = {
  teams: "Search teams",
  leagues: "Search leagues",
  categories: "Search classes",
  seasons: "Search seasons",
};

const dataKeys: Record<DataType, ColumnDefinition[]> = {
  teams: [
    { id: "id", size: 1 },
    { id: "hash", size: 2 },
    { id: "name", size: 4, align: "start" },
    { id: "alpha2", size: 4, align: "start" },
  ],
  leagues: [
    { id: "id", size: 1 },
    { id: "hash", size: 2 },
    { id: "name", size: 4, align: "start" },
    { id: "start_league_timestamp", size: 2 },
    { id: "end_league_timestamp", size: 2 },
  ],
  categories: [
    { id: "id", size: 1 },
    { id: "hash", size: 2 },
    { id: "name", size: 4, align: "start" },
    { id: "alpha2", size: 4, align: "start" },
  ],
  seasons: [
    { id: "id", size: 1 },
    { id: "season", size: 6, align: "start" },
    { id: "season_year", size: 2 },
  ],
};

function getKeyName(key: string): string {
  switch (key) {
    case "alpha2":
      return "Country";
    case "hash":
      return "Image";
    case "season_year":
      return "Year";
    case "start_league_timestamp":
      return "Start Time";
    case "end_league_timestamp":
      return "End Time";
  }

  return toTitleCase(key).replaceAll("Timestamp", "Time");
}

function byEngland(
  alpha2: string,
): { iso2: string; country: string } | undefined {
  if (alpha2 === "EN") {
    return { iso2: "GB", country: "England" };
  }

  return undefined;
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getTextAlignmentClass(align: ColumnDefinition["align"] = "center") {
  return align === "start" ? "text-left" : "text-center";
}

function getFlexAlignmentClass(align: ColumnDefinition["align"] = "center") {
  return align === "start" ? "justify-start" : "justify-center";
}

const SportDataContent: FC<{ content?: string; keyId: string }> = ({
  content,
  keyId,
}) => {
  if (!content) {
    return <span className="text-sm font-medium text-slate-300">-</span>;
  }

  if (keyId.includes("timestamp")) {
    return (
      <span className="whitespace-nowrap text-sm font-medium text-slate-600">
        {formatDate(content)}
      </span>
    );
  }

  if (keyId.includes("hash")) {
    return (
      <div className="flex justify-center">
        <div className="flex h-12 w-12 items-center justify-center">
          <img
            className="h-8 w-8 object-contain"
            src={`https://images.sportmicro.com/${content}.png`}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/png/background.png";
            }}
          />
        </div>
      </div>
    );
  }

  if (keyId.includes("alpha2")) {
    const metadata =
      byEngland(content) ||
      byIso(content) ||
      byInternet(content) ||
      byFips(content);
    const countryName = metadata?.country;
    const iso = metadata?.iso2 ?? "";
    const FlagComponent = countries.includes(iso)
      ? ((flags as any)[iso] as (props: { className: string }) => JSX.Element)
      : undefined;

    return (
      <div className="flex min-w-0 items-center gap-3">
        {FlagComponent ? (
          <FlagComponent className="h-4 w-6 shrink-0 overflow-hidden rounded-[4px] border border-slate-200 object-cover shadow-sm" />
        ) : (
          <div className="h-4 w-6 shrink-0 rounded-[4px] border border-dashed border-slate-200 bg-slate-50" />
        )}

        <span className="truncate text-sm font-medium text-slate-700">
          {countryName ? `${countryName} (${content})` : content}
        </span>
      </div>
    );
  }

  return (
    <span
      className={`block break-words text-sm font-medium ${
        keyId === "id" || keyId === "season_year"
          ? "font-mono text-slate-500"
          : "text-slate-700"
      }`}
    >
      {content}
    </span>
  );
};

const SportDataSet: FC<{
  content: object[];
  type: DataType;
  setSortBy: (prop: string) => void;
  setSortDirection: (d: SortDirection) => void;
  sortBy: string;
  sortDirection: SortDirection;
}> = ({
  content,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
  type,
}) => {
  const keys = dataKeys[type];

  return (
    <div className="h-full overflow-x-auto">
      <table className="min-w-[760px] w-full table-fixed border-separate border-spacing-0">
        <thead>
          <tr>
            {keys.map((key) => {
              const active = sortBy === key.id;

              return (
                <th
                  colSpan={key.size}
                  key={key.id}
                  className={`border-b border-slate-200 bg-slate-50/90 px-4 py-3 ${getTextAlignmentClass(
                    key.align,
                  )}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (sortBy === key.id) {
                        setSortDirection(
                          sortDirection === "ascending"
                            ? "descending"
                            : "ascending",
                        );
                      } else {
                        setSortBy(key.id);
                        setSortDirection("ascending");
                      }
                    }}
                    className={`flex w-full items-center gap-2 text-[11px] font-semibold uppercase text-slate-500 transition hover:text-slate-900 ${getFlexAlignmentClass(
                      key.align,
                    )}`}
                  >
                    <span>{getKeyName(key.id)}</span>
                    {active ? (
                      <span className="rounded-full bg-slate-900 px-2 py-1 text-[10px] tracking-[0.12em] text-white">
                        {sortDirection === "ascending" ? "ASC" : "DESC"}
                      </span>
                    ) : null}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {content.map((row, idx) => (
            <tr
              className={`transition-colors hover:bg-slate-50 ${
                idx % 2 === 0 ? "bg-white" : "bg-slate-50/35"
              }`}
              key={idx}
            >
              {keys
                .map(
                  (key) =>
                    [
                      (row as any)[key.id]?.toString() as string | undefined,
                      key,
                    ] as [string | undefined, ColumnDefinition],
                )
                .map(([cell, key], idx2) => (
                  <td
                    colSpan={key.size}
                    key={idx2}
                    className={`border-b border-slate-100 px-4 py-3.5 align-middle ${getTextAlignmentClass(
                      key.align,
                    )}`}
                  >
                    <SportDataContent content={cell} keyId={key.id} />
                  </td>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const SportQuery: FC<{ sport: Sports }> = ({ sport }) => (
  <QueryClientProvider client={client}>
    <Query sport={sport} />
  </QueryClientProvider>
);

const Query: FC<{ sport: Sports }> = ({ sport }) => {
  const [type, setType] = useState<DataType>("teams");
  const [page, setPage] = useState(0);
  const [property, setSortBy] = useState("id");
  const [direction, setSortDirection] = useState<SortDirection>("ascending");
  const [nameSearch, setNameSearch] = useState("");
  const [name, setName] = useState("");
  const { data, isLoading } = useQuery(
    [type, sport, page, property, direction, name],
    () =>
      getPublicData(sport, (e) =>
        e[type]()
          .property(type === "seasons" ? "season" : "name")
          .insensitive.like(`*${name}*`)
          .limit(fetchLimit)
          .offset(rowsPerPage * page)
          .order(
            (o) => o.property(property)[direction].property(property).nullsLast,
          )
          .count(),
      ),
    {
      cacheTime: 0,
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    },
  );

  const visibleRows = data?.content?.slice(0, rowsPerPage) ?? [];
  const totalResults = data?.pagination?.count ?? visibleRows.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / rowsPerPage));
  const hasNextPage = (data?.content.length ?? 0) > rowsPerPage;
  const summary = isLoading
    ? `Loading ${dataTypeLabels[type].toLowerCase()}...`
    : `${totalResults} ${dataTypeLabels[type].toLowerCase()} available`;

  const resetFilters = () => {
    setPage(0);
    setName("");
    setNameSearch("");
  };

  return (
    <div className="relative w-full overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.24)]">
      <div className="border-b border-slate-200/80 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {dataTypes.map((item) => {
              const active = item === type;

              return (
                <button
                  type="button"
                  key={item}
                  onClick={() => {
                    if (item === type) {
                      return;
                    }

                    setPage(0);
                    setSortBy("id");
                    setSortDirection("ascending");
                    setName("");
                    setNameSearch("");
                    setType(item);
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    active
                      ? "bg-slate-900 text-white shadow-[0_10px_24px_rgba(15,23,42,0.2)]"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {dataTypeLabels[item]}
                </button>
              );
            })}
          </div>

          <div className="text-sm font-medium text-slate-500">{summary}</div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(0);
            setName(nameSearch.trim());
          }}
          className="mt-4 flex flex-col gap-3 sm:flex-row"
        >
          <label className="relative flex-1">
            <img
              src="/svg/search.svg"
              alt=""
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-45"
            />
            <input
              placeholder={searchPlaceholderByType[type]}
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white"
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
          </label>

          <button
            type="submit"
            className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Search
          </button>
        </form>
      </div>

      <div className="relative min-h-[32rem] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)]">
        {!isLoading && data ? (
          visibleRows.length > 0 ? (
            <SportDataSet
              type={type}
              sortBy={property}
              sortDirection={direction}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              content={visibleRows}
            />
          ) : (
            <div className="flex min-h-[32rem] flex-col items-center justify-center px-6 text-center">
              <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                No Results
              </div>
              <p className="mt-4 max-w-md text-sm leading-6 text-slate-500">
                Try a different search term or switch to another dataset.
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Clear Search
              </button>
            </div>
          )
        ) : (
          <div className="relative min-h-[32rem]">
            <Loader resource={type} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200/80 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="text-sm text-slate-500">
          Page <span className="font-semibold text-slate-900">{page + 1}</span>{" "}
          of <span className="font-semibold text-slate-900">{totalPages}</span>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>

          <button
            type="button"
            disabled={!hasNextPage}
            onClick={() =>
              setPage((currentPage) =>
                hasNextPage ? currentPage + 1 : currentPage,
              )
            }
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
